import { defaultMktxDealers } from '../Component/OrderEntry/marketAxessDealers';
import { create } from 'zustand';
import { validateSecurityCusip, fetchAccounts, fetchPMUsers as fetchPMUsersApi, fetchPMAccounts as fetchPMAccountsApi } from '../http/api';
import { TRUMID_PARTIAL_EXECUTION, TRUMID_PRICE_TYPES, TRUMID_RFQ_INQUIRY_TYPES, TRUMID_STIPULATION_TYPES, MKTX_DEALER_LIST_MODES, MKTX_INQUIRY_TYPES, MKTX_PRICE_TYPES, MKTX_PRICING_PROCESSES, MKTX_STRATEGY_TYPES, PROTOCOL_TYPES } from '../Constants/AppConstants';

export const useActualOrderStore = create((set, get) => ({
  actualOrderData: {
    account: '',
    cusip: '',
    cusipRef: '',
    qty: '',
    price: '',
    venue: '',
    side: '',
    protocol: 'CLOB', // Add protocol field with default value
    characteristicId: null,
    comments:'',
    listName: '', // Optional shared MA ladder-list ticket name; excluded from single payloads.
    rejectReason: null, // Reason for rejection (displayed in readonly mode)
    bondLadderId: null, // Add this field to store bond ladder ID
    bondLadderRungId: null, // Rung the order belongs to (ladder-linked characteristics only)
    ttl: 300, // Updated default TTL value from 3000 to 300
    bondInventoryId: null, // Added bondInventoryId field (renamed from inventoryId)
    ladderReferenceCusip: '',
    ladderReferencePrice: '',
    minQty: null, // minimum allowed quantity for this security (populated after CUSIP validation)
    rfqStatus: null, // NEW: RFQ status field
    // Shared RFQ fields (used by both TruMid RFQ and MarketAxess)
    // For TruMid RFQ: ASAP, BIN
    // For MarketAxess: ASAP, BIN, Delayed ASAP
    rfqInquiryType: TRUMID_RFQ_INQUIRY_TYPES.ASAP,
    settlementDate: null,
    // For TruMid RFQ: PRICE, SPREAD
    // For MarketAxess: PRICE, SPREAD, YIELD
    priceType: TRUMID_PRICE_TYPES.SPREAD, // Changed default to SPREAD
    stipulationType: TRUMID_STIPULATION_TYPES.HEDGE,
    allowPartialExecution: TRUMID_PARTIAL_EXECUTION.YES,
    quoteSubmissionDeadline: 5, // Default 5 minutes (valid for both TruMid and MarketAxess dropdown)
    // MarketAxess specific fields (not shared with TruMid RFQ)
    mktxDealerListMode: MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST,
    mktxTargetLevel: null,
    // New: MarketAxess pricing process
    mktxPricingProcess: MKTX_PRICING_PROCESSES.ONESTEP,
    strategyType: MKTX_STRATEGY_TYPES.TAKE,
    effectiveTime: null, // Will be set to UTC+1hr when MarketAxess protocol selected
    expiryTime: null,    // Will be set to UTC+1hr when MarketAxess protocol selected
    strategyParameters: {},          // NEW: dynamic strategy parameter values
    strategyParamDefinitions: [],    // NEW: fetched parameter definitions
    strategyParamsExisting: [],      // NEW: existing RFQ param mappings for edit
    editRfqMode: false,              // NEW: edit RFQ flag
    rfqId: null,                     // NEW: rfq identifier for modify call
    strategyParametersOriginal: {},  // NEW: snapshot of original params
    strategyTypeOriginal: null,      // NEW: snapshot of original strategy type
  },
  accountIdentifiers: [],
  bondDetails: null,
  isLoading: false,
  cusipError: '', // Ensure this is accessible
  fieldErrors: {},
  warningMessage: null,
  fetchedMarketData: null,
  marketDataError: null,

  setActualOrderData: (data) => set((state) => {
    const next = { ...state.actualOrderData, ...data };
    // Apply the default on entry/mode/strategy changes, never over a named selection
    // or a deliberate clear while the trader is editing the same mode.
    if (next.protocol === PROTOCOL_TYPES.MARKETAXESS && !next.editRfqMode &&
        next.strategyType !== MKTX_STRATEGY_TYPES.SEEK &&
        next.strategyType !== MKTX_STRATEGY_TYPES.PROVIDE &&
        (next.protocol !== state.actualOrderData.protocol ||
         next.mktxDealerListMode !== state.actualOrderData.mktxDealerListMode ||
         next.strategyType !== state.actualOrderData.strategyType)) {
      next.venue = defaultMktxDealers(next.venue, next.mktxDealerListMode);
    }
    return { actualOrderData: next };
  }),
  // NEW: replace entire object with API data (no defaults merged)
  setActualOrderDataFromApi: (data) => set(() => ({
    actualOrderData: { 
      ...(data || {}),
      strategyParametersOriginal: data?.strategyParametersOriginal ?? (data?.strategyParameters ? { ...data.strategyParameters } : {}),
      strategyTypeOriginal: data?.strategyTypeOriginal ?? data?.strategyType ?? null,
    }
  })),

  setWarningMessage: (message) => set({ warningMessage: message || null }),
  clearWarningMessage: () => set({ warningMessage: null }),

  setFetchedMarketData: (data) => set({ fetchedMarketData: data }),
  setMarketDataError: (error) => set({ marketDataError: error }),

  fetchAccounts: async (token) => {
    try {
      const response = await fetchAccounts(token);
      const identifiers = response.map(acc => acc.accountIdentifier);
      set({ accountIdentifiers: identifiers });

      if (identifiers.length > 0) {
        get().setActualOrderData({ account: identifiers[0] });
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  },

  validateCusip: async (token) => {
    const { cusip } = get().actualOrderData;
    if (cusip.length < 9) {
      set({ cusipError: 'CUSIP must be at least 9 characters' });
      // Ensure cusipRef is cleared for invalid CUSIP
      get().setActualOrderData({ cusipRef: '' });
      return;
    }

    set({ isLoading: true, cusipError: '' }); // Clear error when starting validation
    try {
      const bondDetails = await validateSecurityCusip(token, cusip);
      
      // Update the store with bondInventoryId if available
      if (bondDetails && bondDetails.id) {
        get().setActualOrderData({ bondInventoryId: bondDetails.id });
      }
      // If API returned a minQty, store it and prefill qty if empty
      if (bondDetails && bondDetails.minQty != null) {
        const minQtyVal = bondDetails.minQty;
        get().setActualOrderData({ minQty: minQtyVal });
        const currentQty = get().actualOrderData.qty;
        if (!currentQty || Number(currentQty) < Number(minQtyVal)) {
          get().setActualOrderData({ qty: `${minQtyVal}` });
        }
      }
      
      set({ bondDetails, cusipError: '' });
    } catch (error) {
      // Clear cusipRef when validation fails
      get().setActualOrderData({ cusipRef: '' });
      set({ cusipError: 'Invalid CUSIP or error fetching bond details' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  // New reset function to clear data
  resetActualOrderData: () =>
    set({
      actualOrderData: {
        account: '',
        cusip: '',
        cusipRef: '',
        qty: '',
        price: '',
        venue: '',
        side: '',
        protocol: 'CLOB',
        characteristicId: null,
        comments:'',
        listName: '',
        rejectReason: null, // Reset reject reason
        bondLadderId: null,
        bondLadderRungId: null,
        ttl: 300,
        bondInventoryId: null,
        ladderReferenceCusip: '',
        ladderReferencePrice: '',
        minQty: null,
        rfqStatus: null, // NEW: reset rfqStatus
        // Shared RFQ fields (used by both TruMid RFQ and MarketAxess)
        rfqInquiryType: TRUMID_RFQ_INQUIRY_TYPES.ASAP,
        settlementDate: null,
        priceType: TRUMID_PRICE_TYPES.SPREAD,
        stipulationType: TRUMID_STIPULATION_TYPES.HEDGE,
        allowPartialExecution: TRUMID_PARTIAL_EXECUTION.YES,
        quoteSubmissionDeadline: 5,
        // MarketAxess specific fields
        mktxDealerListMode: MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST,
        mktxTargetLevel: null,
        mktxPricingProcess: MKTX_PRICING_PROCESSES.ONESTEP,
        strategyType: MKTX_STRATEGY_TYPES.TAKE,
        effectiveTime: null,
        expiryTime: null,
        strategyParameters: {},
        strategyParamDefinitions: [],
        strategyParamsExisting: [],
        editRfqMode: false,  // Ensure this is reset
        rfqId: null,         // Ensure this is reset
        strategyParametersOriginal: {},  // NEW: reset snapshot
        strategyTypeOriginal: null,      // NEW: reset snapshot
      },
      fieldErrors: {
        account: false,
        cusip: false,
        qty: false,
        price: false,
        venue: false,
        side: false,
        protocol: false,
        ttl: false,
        rfqInquiryType: false,
        quoteSubmissionDeadline: false,
        priceType: false,
        stipulationType: false,
        allowPartialExecution: false,
        mktxDealerListMode: false,
        mktxTargetLevel: false,
        mktxPricingProcess: false,
        strategyType: false,
        effectiveTime: false,
        expiryTime: false,
        strategyParameters: {},
      },
      accountIdentifiers: [],
      bondDetails: null,
      isLoading: false,
      cusipError: '', // Reset CUSIP error on clear
      warningMessage: null,
      fetchedMarketData: null,
      marketDataError: null,
    }),
  setFieldErrors: (errors) => set({ fieldErrors: errors }),
  clearFieldErrors: () => set({ fieldErrors: {} }),
  clearFieldError: (fieldName, nestedKey) => set(state => {
    const next = { ...state.fieldErrors };
    if (nestedKey && typeof next[fieldName] === 'object') {
      next[fieldName] = { ...next[fieldName], [nestedKey]: false };
    } else {
      next[fieldName] = false;
    }
    return { fieldErrors: next };
  }),
  setCusipError: (error) => set({ cusipError: error }), // Ensure setCusipError method exists
}));
